import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faHospital,
  faLock,
  faPencil,
  faPlaneDeparture,
  faSpinner,
  faTimes,
  faTrash,
  faUnlock,
  faUserCog,
  faUserMinus,
  faUserPlus,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import {
  BulkAddToClinicModal,
  BulkRemoveFromClinicModal,
  CustomDropdown,
  StatusBadge,
  Table,
  UserStatus,
  createCustomColumn,
  createDateColumn,
  createEnumColumn,
  createTextColumn,
  useTableState,
} from '../deps.js';
import AddUserModal from './AddUserModal.js';
import BulkAddUsersModal from './BulkAddUsersModal.js';
import ResetPasswordModal from './ResetPasswordModal.js';
import SetAvailabilityModal from '../../common/SetAvailabilityModal.js';
import DeleteUserModal from './DeleteUserModal.js';
import BulkResetPasswordModal from './BulkResetPasswordModal.js';
import BulkDeleteUserModal from './BulkDeleteUserModal.js';
import BulkSetRoleModal from './BulkSetRoleModal.js';
import BulkAddToTeamModal from './BulkAddToTeamModal.js';
import { availabilityLabel } from '../../utils/outOfOffice.js';

const useAdminUsers = (directory) => {
  const {
    clinics,
    adminSelectedClinicId,
    users,
    setUsers,
    teams,
    roles,
    clinicHeaderOverride,
    identityService,
    authService,
    teamService,
    reloadUsers,
    reloadTeams,
    addNotification,
    isMaster,
    tableIds,
    loading,
  } = directory;

  const [editingUser, setEditingUser] = useState(null);
  const [editableName, setEditableName] = useState('');
  const [availabilityUser, setAvailabilityUser] = useState(null);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState({});
  const [updateLoading, setUpdateLoading] = useState({});
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [bulkAddUsersModalOpen, setBulkAddUsersModalOpen] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [bulkOnboardingLoading, setBulkOnboardingLoading] = useState(false);
  const [unblockLoading, setUnblockLoading] = useState({});
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetLoading, setResetLoading] = useState({});
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkResetPasswordModalOpen, setBulkResetPasswordModalOpen] = useState(false);
  const [bulkDeleteUserModalOpen, setBulkDeleteUserModalOpen] = useState(false);
  const [bulkSetRoleModalOpen, setBulkSetRoleModalOpen] = useState(false);
  const [bulkAddToTeamModalOpen, setBulkAddToTeamModalOpen] = useState(false);
  const [usersForBulkAction, setUsersForBulkAction] = useState([]);
  const [bulkResetLoading, setBulkResetLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkSetRoleLoading, setBulkSetRoleLoading] = useState(false);
  const [bulkAddToTeamLoading, setBulkAddToTeamLoading] = useState(false);
  const [bulkAddToClinicModalOpen, setBulkAddToClinicModalOpen] = useState(false);
  const [bulkAddToClinicLoading, setBulkAddToClinicLoading] = useState(false);
  const [bulkRemoveFromClinicModalOpen, setBulkRemoveFromClinicModalOpen] = useState(false);
  const [bulkRemoveFromClinicLoading, setBulkRemoveFromClinicLoading] = useState(false);

  const {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
  } = useTableState(tableIds.users, {
    defaultSortState: { columnKey: 'name', direction: 'asc' },
  });

  useEffect(() => {
    setSelectedRows(new Set());
  }, [adminSelectedClinicId]);

  const toggleRoleDropdown = useCallback((userId) => {
    setRoleDropdownOpen((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });
      newState[userId] = !prev[userId];
      return newState;
    });
  }, []);

  const handleEditToggle = useCallback((user) => {
    if (editingUser && editingUser.userId === user.userId) {
      setEditingUser(null);
      setEditableName('');
    } else {
      setEditingUser(user);
      setEditableName(user.name);
    }
  }, [editingUser]);

  const handleNameSave = useCallback((user) => {
    const userId = user.userId;
    setUpdateLoading((prev) => ({ ...prev, [userId]: true }));

    identityService.updateUserDetails(userId, { name: editableName })
      .then(() => {
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.userId === userId ? { ...u, name: editableName } : u
          )
        );
        setEditingUser(null);
        setUpdateLoading((prev) => ({ ...prev, [userId]: false }));
        addNotification(`user-update-${userId}`, 'User name updated successfully');
      }).catch(() => {
        console.error(`Failed to update user ${userId}:`);
        setUpdateLoading((prev) => ({ ...prev, [userId]: false }));
        addNotification(`user-update-error-${userId}`, 'Failed to update user name', { isError: true });
      });
  }, [addNotification, editableName, identityService, setUsers]);

  const handleAvailabilitySave = useCallback(async ({ markOutOfOffice, returnsAt }) => {
    if (!availabilityUser) {
      return;
    }
    const userId = availabilityUser.userId;
    try {
      await identityService.updateUserAvailability(userId, { markOutOfOffice, returnsAt });
      const outOfOffice = markOutOfOffice ? { returnsAt: returnsAt ?? null } : null;
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.userId === userId ? { ...u, outOfOffice } : u))
      );
      setAvailabilityUser(null);
      addNotification(`availability-update-${userId}`, 'Availability updated successfully');
    } catch (error) {
      console.error(`Failed to update availability for user ${userId}:`, error);
      addNotification(`availability-update-error-${userId}`, 'Failed to update availability', { isError: true });
      throw error;
    }
  }, [addNotification, availabilityUser, identityService, setUsers]);

  const handleRoleChange = useCallback((user, roleIdentifier) => {
    const userId = user.userId;
    setUpdateLoading((prev) => ({ ...prev, [userId]: true }));

    identityService.updateUserRole(userId, roleIdentifier)
      .then(() => {
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.userId === userId ? { ...u, role: roleIdentifier } : u
          )
        );
        setRoleDropdownOpen((prev) => ({ ...prev, [userId]: false }));
        setUpdateLoading((prev) => ({ ...prev, [userId]: false }));
        addNotification(`role-update-${userId}`, 'User role updated successfully');
      })
      .catch(() => {
        console.error(`Failed to update role for user ${userId}:`);
        setRoleDropdownOpen((prev) => ({ ...prev, [userId]: false }));
        setUpdateLoading((prev) => ({ ...prev, [userId]: false }));
        addNotification(`role-update-error-${userId}`, 'Failed to update user role', { isError: true });
      });
  }, [addNotification, identityService, setUsers]);

  const handleAddUserSubmit = useCallback(async (email, name, role, clinicId) => {
    setOnboardingLoading(true);
    try {
      await authService.onboardUser(email.toLowerCase(), name, role, [clinicId || adminSelectedClinicId]);
      await reloadUsers();
      setSelectedRows(new Set());
      setAddUserModalOpen(false);
      setOnboardingLoading(false);
      addNotification(`user-onboard-${email}`, 'User added successfully');
    } catch (error) {
      console.error('Failed to onboard user:', error);
      setOnboardingLoading(false);
      addNotification('user-onboard-error', 'Failed to add user', { isError: true });
    }
  }, [addNotification, adminSelectedClinicId, authService, reloadUsers]);

  const handleBulkAddUsersSubmit = useCallback((usersToAdd, role) => {
    if (usersToAdd.length === 0 || !role) return;
    setBulkOnboardingLoading(true);

    const onboardPromises = usersToAdd.map(({ email, name }) =>
      authService.onboardUser(email.toLowerCase(), name, role, [adminSelectedClinicId])
        .then(() => {
          addNotification(`user-onboard-${email}`, `${name} added successfully`);
        })
        .catch((error) => {
          console.error(`Failed to onboard user ${email}:`, error);
          addNotification(`user-onboard-error-${email}`, `Failed to add ${name}`, { isError: true });
          throw error;
        })
    );

    Promise.allSettled(onboardPromises)
      .then(async () => {
        await reloadUsers();
        setSelectedRows(new Set());
        setBulkAddUsersModalOpen(false);
      })
      .finally(() => {
        setBulkOnboardingLoading(false);
      });
  }, [addNotification, adminSelectedClinicId, authService, reloadUsers]);

  const handleUnblockUser = useCallback((user) => {
    const userId = user.userId;
    setUnblockLoading((prev) => ({ ...prev, [userId]: true }));

    identityService.unblockUser(userId)
      .then(() => {
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.userId === userId ? { ...u, userStatus: UserStatus.PENDING } : u
          )
        );
        setUnblockLoading((prev) => ({ ...prev, [userId]: false }));
        addNotification(`user-unblock-${userId}`, 'User unblocked successfully');
      }).catch(() => {
        console.error(`Failed to unblock user ${userId}:`);
        setUnblockLoading((prev) => ({ ...prev, [userId]: false }));
        addNotification(`user-unblock-error-${userId}`, 'Failed to unblock user', { isError: true });
      });
  }, [addNotification, identityService, setUsers]);

  const handleResetPasswordClick = useCallback((user) => {
    setUserToReset(user);
    setResetPasswordModalOpen(true);
  }, []);

  const handleResetPasswordConfirm = useCallback(() => {
    if (!userToReset) return;
    const userId = userToReset.userId;
    setResetLoading((prev) => ({ ...prev, [userId]: true }));

    identityService.resetUser(userId)
      .then(() => {
        setResetLoading((prev) => ({ ...prev, [userId]: false }));
        setResetPasswordModalOpen(false);
        setUserToReset(null);
        addNotification(`user-reset-${userId}`, 'Password reset instructions sent to user');
      }).catch(() => {
        console.error(`Failed to reset password for user ${userId}:`);
        setResetLoading((prev) => ({ ...prev, [userId]: false }));
        setResetPasswordModalOpen(false);
        setUserToReset(null);
        addNotification(`user-reset-error-${userId}`, 'Failed to reset user password', { isError: true });
      });
  }, [addNotification, identityService, userToReset]);

  const handleDeleteUserClick = useCallback((user) => {
    setUserToDelete(user);
    setDeleteUserModalOpen(true);
  }, []);

  const handleDeleteUserConfirm = useCallback(() => {
    if (!userToDelete) return;
    const userId = userToDelete.userId;
    setDeleteLoading((prev) => ({ ...prev, [userId]: true }));

    identityService.deleteUser(userId)
      .then(() => {
        setUsers((prevUsers) => prevUsers.filter((u) => u.userId !== userId));
        setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
        setDeleteUserModalOpen(false);
        setUserToDelete(null);
        addNotification(`user-delete-${userId}`, 'User deleted successfully');
      }).catch(() => {
        console.error(`Failed to delete user ${userId}:`);
        setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
        setDeleteUserModalOpen(false);
        setUserToDelete(null);
        addNotification(`user-delete-error-${userId}`, 'Failed to delete user', { isError: true });
      });
  }, [addNotification, identityService, setUsers, userToDelete]);

  const handleBulkResetPassword = useCallback((selectedUsersData) => {
    if (selectedUsersData.length === 0) return;
    setUsersForBulkAction(selectedUsersData);
    setBulkResetPasswordModalOpen(true);
  }, []);

  const handleBulkDeleteUsers = useCallback((selectedUsersData) => {
    if (selectedUsersData.length === 0) return;
    setUsersForBulkAction(selectedUsersData);
    setBulkDeleteUserModalOpen(true);
  }, []);

  const handleBulkSetRole = useCallback((selectedUsersData) => {
    if (selectedUsersData.length === 0) return;
    setUsersForBulkAction(selectedUsersData);
    setBulkSetRoleModalOpen(true);
  }, []);

  const handleBulkAddToTeam = useCallback((selectedUsersData) => {
    if (selectedUsersData.length === 0) return;
    setUsersForBulkAction(selectedUsersData);
    setBulkAddToTeamModalOpen(true);
  }, []);

  const handleBulkAddToClinic = useCallback((selectedUsersData) => {
    if (selectedUsersData.length === 0) return;
    setUsersForBulkAction(selectedUsersData);
    setBulkAddToClinicModalOpen(true);
  }, []);

  const handleBulkRemoveFromClinic = useCallback((selectedUsersData) => {
    if (selectedUsersData.length === 0) return;
    setUsersForBulkAction(selectedUsersData);
    setBulkRemoveFromClinicModalOpen(true);
  }, []);

  const confirmBulkResetPassword = useCallback(() => {
    if (usersForBulkAction.length === 0) return;
    setBulkResetLoading(true);

    const resetPromises = usersForBulkAction.map((user) =>
      new Promise((resolve, reject) => {
        identityService.resetUser(user.userId)
          .then(() => {
            addNotification(`bulk-reset-${user.userId}`, `Password reset sent to ${user.name}`);
            resolve(user);
          }).catch((error) => {
            console.error(`Failed to reset password for user ${user.userId}:`, error);
            addNotification(`bulk-reset-error-${user.userId}`, `Failed to reset password for ${user.name}`, { isError: true });
            reject(error);
          });
      })
    );

    Promise.allSettled(resetPromises).then(() => {
      setSelectedRows(new Set());
      setBulkResetPasswordModalOpen(false);
      setUsersForBulkAction([]);
    }).finally(() => {
      setBulkResetLoading(false);
    });
  }, [addNotification, identityService, usersForBulkAction]);

  const confirmBulkDeleteUsers = useCallback(() => {
    if (usersForBulkAction.length === 0) return;
    setBulkDeleteLoading(true);

    const deletePromises = usersForBulkAction.map((user) =>
      new Promise((resolve, reject) => {
        identityService.deleteUser(user.userId)
          .then(() => {
            addNotification(`bulk-delete-${user.userId}`, `${user.name} deleted successfully`);
            resolve(user);
          }).catch((error) => {
            console.error(`Failed to delete user ${user.userId}:`, error);
            addNotification(`bulk-delete-error-${user.userId}`, `Failed to delete ${user.name}`, { isError: true });
            reject(error);
          });
      })
    );

    Promise.allSettled(deletePromises).then(() => {
      const deletedUserIds = usersForBulkAction.map((user) => user.userId);
      setUsers((prevUsers) => prevUsers.filter((user) => !deletedUserIds.includes(user.userId)));
      setSelectedRows(new Set());
      setBulkDeleteUserModalOpen(false);
      setUsersForBulkAction([]);
    }).finally(() => {
      setBulkDeleteLoading(false);
    });
  }, [addNotification, identityService, setUsers, usersForBulkAction]);

  const confirmBulkSetRole = useCallback((newRole) => {
    if (usersForBulkAction.length === 0 || !newRole) return;
    setBulkSetRoleLoading(true);

    const roleUpdatePromises = usersForBulkAction.map((user) =>
      new Promise((resolve, reject) => {
        identityService.updateUserRole(user.userId, newRole)
          .then(() => {
            addNotification(`bulk-role-${user.userId}`, `${user.name} role updated to ${newRole}`);
            resolve(user);
          }).catch((error) => {
            console.error(`Failed to update role for user ${user.userId}:`, error);
            addNotification(`bulk-role-error-${user.userId}`, `Failed to update role for ${user.name}`, { isError: true });
            reject(error);
          });
      })
    );

    Promise.allSettled(roleUpdatePromises).then(() => {
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          usersForBulkAction.find((bulkUser) => bulkUser.userId === user.userId)
            ? { ...user, role: newRole }
            : user
        )
      );
      setSelectedRows(new Set());
      setBulkSetRoleModalOpen(false);
      setUsersForBulkAction([]);
    }).finally(() => {
      setBulkSetRoleLoading(false);
    });
  }, [addNotification, identityService, setUsers, usersForBulkAction]);

  const confirmBulkAddToTeam = useCallback((teamId) => {
    if (usersForBulkAction.length === 0 || !teamId) return;
    setBulkAddToTeamLoading(true);

    const userIds = usersForBulkAction.map((user) => user.userId);
    const teamUpdate = {
      teamId,
      teamName: undefined,
      addedUserIds: userIds,
      removedUserIds: undefined,
    };

    teamService.updateTeam(teamUpdate, clinicHeaderOverride)
      .then(() => {
        const team = teams.find((t) => t.teamId === teamId);
        const teamName = team?.teamName || 'team';
        reloadTeams();
        setSelectedRows(new Set());
        setBulkAddToTeamModalOpen(false);
        setUsersForBulkAction([]);
        addNotification(`bulk-add-to-team-${teamId}`, `${userIds.length} user${userIds.length > 1 ? 's' : ''} added to ${teamName}`);
      })
      .catch((error) => {
        console.error(`Failed to add users to team ${teamId}:`, error);
        addNotification(`bulk-add-to-team-error-${teamId}`, 'Failed to add users to team', { isError: true });
      })
      .finally(() => {
        setBulkAddToTeamLoading(false);
      });
  }, [addNotification, clinicHeaderOverride, reloadTeams, teamService, teams, usersForBulkAction]);

  const confirmBulkAddToClinic = useCallback(async (targetClinicIds) => {
    if (usersForBulkAction.length === 0 || targetClinicIds.length === 0) return;
    setBulkAddToClinicLoading(true);

    try {
      let totalAdded = 0;
      for (const userData of usersForBulkAction) {
        const existingClinicIds = new Set(userData.clinicIds || []);
        const delta = targetClinicIds.filter((id) => !existingClinicIds.has(id));
        if (delta.length === 0) continue;
        await identityService.addUserToClinics(userData.userId, delta);
        totalAdded++;
      }

      await reloadUsers();
      setSelectedRows(new Set());
      setBulkAddToClinicModalOpen(false);
      setUsersForBulkAction([]);

      if (totalAdded > 0) {
        addNotification('bulk-add-to-clinic', `${totalAdded} user${totalAdded > 1 ? 's' : ''} updated with new clinic assignments`);
      } else {
        addNotification('bulk-add-to-clinic-noop', 'All selected users are already in the chosen clinics');
      }
    } catch (error) {
      console.error('Failed to add users to clinics:', error);
      addNotification('bulk-add-to-clinic-error', 'Failed to add users to clinics', { isError: true });
    } finally {
      setBulkAddToClinicLoading(false);
    }
  }, [addNotification, identityService, reloadUsers, usersForBulkAction]);

  const confirmBulkRemoveFromClinic = useCallback(async (targetClinicIds) => {
    if (usersForBulkAction.length === 0 || targetClinicIds.length === 0) return;
    setBulkRemoveFromClinicLoading(true);

    try {
      let totalRemoved = 0;
      for (const userData of usersForBulkAction) {
        const existingClinicIds = new Set(userData.clinicIds || []);
        const delta = targetClinicIds.filter((id) => existingClinicIds.has(id));
        if (delta.length === 0) continue;
        await identityService.removeUserFromClinics(userData.userId, delta);
        totalRemoved++;
      }

      await reloadUsers();
      setSelectedRows(new Set());
      setBulkRemoveFromClinicModalOpen(false);
      setUsersForBulkAction([]);

      if (totalRemoved > 0) {
        addNotification('bulk-remove-from-clinic', `${totalRemoved} user${totalRemoved > 1 ? 's' : ''} updated with clinic removals`);
      } else {
        addNotification('bulk-remove-from-clinic-noop', 'No selected users were in the chosen clinics');
      }
    } catch (error) {
      console.error('Failed to remove users from clinics:', error);
      addNotification('bulk-remove-from-clinic-error', 'Failed to remove users from clinics', { isError: true });
    } finally {
      setBulkRemoveFromClinicLoading(false);
    }
  }, [addNotification, identityService, reloadUsers, usersForBulkAction]);

  const columns = useMemo(() => [
    createCustomColumn(
      'name',
      'Name',
      (user) => user.name,
      (value, user) => (
        editingUser && editingUser.userId === user.userId ? (
          <div className="admin-edit-name-container">
            <input
              type="text"
              className="admin-name-edit-input"
              value={editableName}
              onChange={(e) => setEditableName(e.target.value)}
            />
            <div className="admin-edit-controls">
              <button
                className="admin-button admin-save-button"
                onClick={() => handleNameSave(user)}
                disabled={updateLoading[user.userId]}
              >
                {updateLoading[user.userId] ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faCheck} />
                )}
              </button>
              <button
                className="admin-button admin-cancel-button"
                onClick={() => handleEditToggle(user)}
                disabled={updateLoading[user.userId]}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        ) : (
          <div className="admin-name-display">
            <span>{user.name}</span>
            <button
              className="admin-button admin-edit-button"
              onClick={() => handleEditToggle(user)}
              disabled={updateLoading[user.userId]}
            >
              <FontAwesomeIcon icon={faPencil} />
            </button>
          </div>
        )
      ),
      {
        sortable: true,
        filterable: true,
        dataType: 'text',
      }
    ),
    createTextColumn(
      'availability',
      'Availability',
      (user) => availabilityLabel(user.outOfOffice)
    ),
    createTextColumn(
      'email',
      'Email',
      (user) => user.email
    ),
    createDateColumn(
      'lastSignedIn',
      'Last Signed In',
      (user) => user.lastSignedIn,
      {
        renderer: (value, user) => {
          if (!user.lastSignedIn) {
            return <span className="admin-last-signed-in-never">Never</span>;
          }

          const date = new Date(user.lastSignedIn);
          const now = new Date();
          const diffInMs = now - date;
          const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
          const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
          const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

          let relativeTime;
          if (diffInDays > 0) {
            relativeTime = `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
          } else if (diffInHours > 0) {
            relativeTime = `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
          } else if (diffInMinutes > 0) {
            relativeTime = `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
          } else {
            relativeTime = 'Just now';
          }

          return (
            <div className="admin-last-signed-in-container">
              <span className="admin-last-signed-in-relative" title={date.toLocaleString()}>
                {relativeTime}
              </span>
              <span className="admin-last-signed-in-absolute">
                {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          );
        },
        compareFn: (a, b) => (a.lastSignedIn || 0) - (b.lastSignedIn || 0),
      }
    ),
    createEnumColumn(
      'userStatus',
      'User Status',
      (user) => user.userStatus || UserStatus.ACTIVE,
      [UserStatus.ACTIVE, UserStatus.PENDING, UserStatus.BLOCKED],
      {
        renderer: (value, user) => (
          <div className="admin-status-container">
            <StatusBadge
              label={user.userStatus || 'Active'}
              colorFamily={user.userStatus === UserStatus.PENDING ? 'yellow' : user.userStatus === UserStatus.BLOCKED ? 'red' : 'green'}
              size="normal"
              tooltip={user.userStatus === UserStatus.BLOCKED ? 'User repeatedly failed verification code' : null}
            />
            {user.userStatus === UserStatus.BLOCKED && (
              <button
                className="admin-button admin-unblock-button"
                onClick={() => handleUnblockUser(user)}
                disabled={unblockLoading[user.userId]}
              >
                {unblockLoading[user.userId] ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUnlock} />
                    <span style={{ marginLeft: '4px' }}>Unblock</span>
                  </>
                )}
              </button>
            )}
          </div>
        ),
        sortable: true,
      }
    ),
    createCustomColumn(
      'actions',
      'Actions',
      (user) => user,
      (value, user) => (
        <div className="admin-row-actions">
          <button
            className="admin-button admin-availability-button"
            onClick={() => setAvailabilityUser(user)}
            title="Set availability"
          >
            <FontAwesomeIcon icon={faPlaneDeparture} />
          </button>
          <button
            className={`admin-button admin-reset-button ${resetLoading[user.userId] ? 'admin-disabled-button' : ''}`}
            onClick={() => handleResetPasswordClick(user)}
            disabled={resetLoading[user.userId]}
            title="Reset Password"
          >
            {resetLoading[user.userId] ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faLock} />
            )}
          </button>
          <button
            className={`admin-button admin-delete-button ${deleteLoading[user.userId] ? 'admin-disabled-button' : ''}`}
            onClick={() => handleDeleteUserClick(user)}
            disabled={deleteLoading[user.userId]}
            title="Delete User"
          >
            {deleteLoading[user.userId] ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faTrash} />
            )}
          </button>
        </div>
      ),
      {
        sortable: false,
        filterable: false,
        className: 'actions-column',
      }
    ),
    createEnumColumn(
      'role',
      'Role',
      (user) => user.role,
      Object.keys(roles),
      {
        renderer: (value, user) => (
          updateLoading[user.userId] ? (
            <div className="custom-select admin-role-select">
              <div className="select-selected">
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
            </div>
          ) : (
            <CustomDropdown
              selected={user.role ? (roles[user.role]?.title || user.role) : 'Unknown'}
              options={
                Object.entries(roles)
                  .map(([roleId, roleData]) => ({
                    id: roleId,
                    display: roleData.title || roleId,
                    value: roleId,
                    isSelected: user.role === roleId,
                  }))
                  .sort((a, b) => a.display.localeCompare(b.display))
              }
              isOpen={roleDropdownOpen[user.userId] || false}
              toggleDropdown={() => toggleRoleDropdown(user.userId)}
              onSelect={(option) => handleRoleChange(user, option.value)}
              className="admin-role-select"
              keyExtractor={(option) => option.id}
              disabled={updateLoading[user.userId]}
            />
          )
        ),
        sortable: true,
      }
    ),
  ], [
    deleteLoading,
    editableName,
    editingUser,
    handleDeleteUserClick,
    handleEditToggle,
    handleNameSave,
    handleResetPasswordClick,
    handleRoleChange,
    handleUnblockUser,
    resetLoading,
    roleDropdownOpen,
    roles,
    toggleRoleDropdown,
    unblockLoading,
    updateLoading,
  ]);

  const bulkActions = useMemo(() => [
    {
      key: 'addToClinic',
      label: 'Add to Clinic',
      icon: faHospital,
      onClick: handleBulkAddToClinic,
    },
    {
      key: 'removeFromClinic',
      label: 'Remove from Clinic',
      icon: faUserMinus,
      onClick: handleBulkRemoveFromClinic,
    },
    {
      key: 'addToTeam',
      label: 'Add to Team',
      icon: faUsers,
      onClick: handleBulkAddToTeam,
    },
    {
      key: 'resetPassword',
      label: 'Reset Passwords',
      icon: faLock,
      onClick: handleBulkResetPassword,
    },
    {
      key: 'setRole',
      label: 'Set Role',
      icon: faUserCog,
      onClick: handleBulkSetRole,
    },
    {
      key: 'deleteUsers',
      label: 'Delete Users',
      icon: faTrash,
      onClick: handleBulkDeleteUsers,
    },
  ], [
    handleBulkAddToClinic,
    handleBulkAddToTeam,
    handleBulkDeleteUsers,
    handleBulkRemoveFromClinic,
    handleBulkResetPassword,
    handleBulkSetRole,
  ]);

  const selectedClinicName = clinics.find((c) => c.clinicId === adminSelectedClinicId)?.clinicName || '';

  const headerActions = isMaster && !adminSelectedClinicId ? null : (
    <>
      <button className="admin-add-user-button" onClick={() => setAddUserModalOpen(true)}>
        <FontAwesomeIcon icon={faUserPlus} /> Add User
      </button>
      {!isMaster && (
        <button className="admin-add-user-button" onClick={() => setBulkAddUsersModalOpen(true)}>
          <FontAwesomeIcon icon={faUsers} /> Add Users
        </button>
      )}
    </>
  );

  const showClinicInTitle = adminSelectedClinicId && (isMaster || clinics.length > 1);
  const usersTable = (
    <Table
      id={tableIds.users}
      data={users}
      columns={columns}
      getRowKey={(user) => user.userId}
      sortState={sortState}
      onSortChange={setSortState}
      filterValues={filterValues}
      onFilterChange={setFilterValues}
      disabledFilters={disabledFilters}
      onDisabledFiltersChange={setDisabledFilters}
      paginationState={paginationState}
      onPaginationChange={setPaginationState}
      enableSelection={true}
      bulkActions={bulkActions}
      selectedRows={selectedRows}
      onSelectionChange={setSelectedRows}
      emptyMessage="No users found."
    />
  );

  const section = (
    <div className="admin-table-section">
      <h2 className="admin-table-title">
        Users{showClinicInTitle ? ` — ${selectedClinicName}` : ''}
      </h2>
      {isMaster && loading.users ? (
        <div className="admin-loading">Loading users...</div>
      ) : usersTable}
    </div>
  );

  const closeBulkUsers = () => {
    setUsersForBulkAction([]);
  };

  const modals = (
    <>
      <AddUserModal
        isOpen={addUserModalOpen}
        onClose={() => setAddUserModalOpen(false)}
        onSubmit={handleAddUserSubmit}
        roles={roles}
        clinics={isMaster ? clinics : null}
        loading={onboardingLoading}
      />
      {!isMaster && (
      <BulkAddUsersModal
        isOpen={bulkAddUsersModalOpen}
        onClose={() => setBulkAddUsersModalOpen(false)}
        onSubmit={handleBulkAddUsersSubmit}
        roles={roles}
        loading={bulkOnboardingLoading}
        existingUserEmails={users.map((user) => user.email)}
      />
      )}
      <SetAvailabilityModal
        isOpen={!!availabilityUser}
        onClose={() => setAvailabilityUser(null)}
        onSave={handleAvailabilitySave}
        user={availabilityUser}
      />
      <ResetPasswordModal
        isOpen={resetPasswordModalOpen}
        onClose={() => {
          setResetPasswordModalOpen(false);
          setUserToReset(null);
        }}
        onConfirm={handleResetPasswordConfirm}
        user={userToReset}
        loading={resetLoading[userToReset?.userId]}
      />
      <DeleteUserModal
        isOpen={deleteUserModalOpen}
        onClose={() => {
          setDeleteUserModalOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteUserConfirm}
        user={userToDelete}
        loading={deleteLoading[userToDelete?.userId]}
      />
      <BulkResetPasswordModal
        isOpen={bulkResetPasswordModalOpen}
        onClose={() => {
          setBulkResetPasswordModalOpen(false);
          closeBulkUsers();
        }}
        onConfirm={confirmBulkResetPassword}
        users={usersForBulkAction}
        loading={bulkResetLoading}
      />
      <BulkDeleteUserModal
        isOpen={bulkDeleteUserModalOpen}
        onClose={() => {
          setBulkDeleteUserModalOpen(false);
          closeBulkUsers();
        }}
        onConfirm={confirmBulkDeleteUsers}
        users={usersForBulkAction}
        loading={bulkDeleteLoading}
      />
      <BulkSetRoleModal
        isOpen={bulkSetRoleModalOpen}
        onClose={() => {
          setBulkSetRoleModalOpen(false);
          closeBulkUsers();
        }}
        onConfirm={confirmBulkSetRole}
        users={usersForBulkAction}
        roles={roles}
        loading={bulkSetRoleLoading}
      />
      <BulkAddToTeamModal
        isOpen={bulkAddToTeamModalOpen}
        onClose={() => {
          setBulkAddToTeamModalOpen(false);
          closeBulkUsers();
        }}
        onConfirm={confirmBulkAddToTeam}
        users={usersForBulkAction}
        teams={teams}
        loading={bulkAddToTeamLoading}
      />
      <BulkAddToClinicModal
        isOpen={bulkAddToClinicModalOpen}
        onClose={() => {
          setBulkAddToClinicModalOpen(false);
          closeBulkUsers();
        }}
        onConfirm={confirmBulkAddToClinic}
        users={usersForBulkAction}
        clinics={clinics}
        loading={bulkAddToClinicLoading}
      />
      <BulkRemoveFromClinicModal
        isOpen={bulkRemoveFromClinicModalOpen}
        onClose={() => {
          setBulkRemoveFromClinicModalOpen(false);
          closeBulkUsers();
        }}
        onConfirm={confirmBulkRemoveFromClinic}
        users={usersForBulkAction}
        clinics={clinics}
        loading={bulkRemoveFromClinicLoading}
      />
    </>
  );

  return { headerActions, section, modals };
};

export default useAdminUsers;
